// Hook to fetch billing data (expenses, time entries, bills) scoped to a single matter.
// Uses direct fetch (not the global billingStore) to avoid overwriting dashboard data.
import { useState, useEffect, useCallback, useRef } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import type {
  Expense,
  TimeEntry,
  Bill,
  CreateTimeEntryDto,
  CreateExpenseDto,
  CreateManualBillDto,
  ClientTrustLedger,
  TrustTransaction,
  RecordTrustDepositDto,
  RecordTrustRefundDto,
  RecordFeeTransferDto,
} from '../../billing/types/billing.types';

const API_BASE = '';
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const isValidUuid = (value?: string | null): value is string =>
  typeof value === 'string' && UUID_PATTERN.test(value.trim());

export interface MatterBillingStats {
  // Expense stats
  totalExpenses: number;
  unbilledExpenses: number;
  billedExpenses: number;
  expenseCount: number;
  // Time entry stats
  totalTimeAmount: number;
  totalHours: number;
  billableHours: number;
  unbilledTimeAmount: number;
  billedTimeAmount: number;
  timeEntryCount: number;
  // Bill stats
  billsTotal: number;
  billsOutstanding: number;
  billsPaid: number;
  billCount: number;
  // Trust stats
  trustBalance: number;
  trustTotalDeposits: number;
  trustTotalDisbursements: number;
  trustTransactionCount: number;
  // Combined
  totalWip: number;
  totalBilled: number;
}

const EMPTY_STATS: MatterBillingStats = {
  totalExpenses: 0,
  unbilledExpenses: 0,
  billedExpenses: 0,
  expenseCount: 0,
  totalTimeAmount: 0,
  totalHours: 0,
  billableHours: 0,
  unbilledTimeAmount: 0,
  billedTimeAmount: 0,
  timeEntryCount: 0,
  billsTotal: 0,
  billsOutstanding: 0,
  billsPaid: 0,
  billCount: 0,
  trustBalance: 0,
  trustTotalDeposits: 0,
  trustTotalDisbursements: 0,
  trustTransactionCount: 0,
  totalWip: 0,
  totalBilled: 0,
};

export function useMatterBilling(matterId: string) {
  const { keycloak } = useKeycloak();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [bills, setBills] = useState<Bill[]>([]);
  const [trustLedgers, setTrustLedgers] = useState<ClientTrustLedger[]>([]);
  const [trustTransactions, setTrustTransactions] = useState<TrustTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<MatterBillingStats>(EMPTY_STATS);

  // Track latest matterId to avoid stale state updates
  const matterIdRef = useRef(matterId);
  matterIdRef.current = matterId;

  const getHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${keycloak?.token || ''}`,
  }), [keycloak?.token]);

  // ── Fetch all billing data for the matter ──

  const fetchAll = useCallback(async () => {
    if (!isValidUuid(matterId) || !keycloak?.token) {
      setExpenses([]);
      setTimeEntries([]);
      setBills([]);
      setTrustLedgers([]);
      setTrustTransactions([]);
      setStats(EMPTY_STATS);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({ matter_id: matterId });
      const headers = getHeaders();

      const [expRes, teRes, billRes, trustLedgerRes, trustTxnRes] = await Promise.all([
        fetch(`${API_BASE}/api/v1/billing/expenses?${params}`, { headers }),
        fetch(`${API_BASE}/api/v1/billing/time-entries?${params}`, { headers }),
        fetch(`${API_BASE}/api/v1/billing/bills?${params}`, { headers }),
        fetch(`${API_BASE}/api/v1/billing/trust-accounts/ledgers/all?${params}`, { headers }).catch(() => null),
        fetch(`${API_BASE}/api/v1/billing/trust-transactions?${params}`, { headers }).catch(() => null),
      ]);

      if (!expRes.ok) throw new Error(`Failed to fetch expenses: ${expRes.statusText}`);
      if (!teRes.ok) throw new Error(`Failed to fetch time entries: ${teRes.statusText}`);
      if (!billRes.ok) throw new Error(`Failed to fetch bills: ${billRes.statusText}`);

      const [expJson, teJson, billJson] = await Promise.all([
        expRes.json(),
        teRes.json(),
        billRes.json(),
      ]);

      // Trust data is optional — may not be set up yet
      const trustLedgerJson = trustLedgerRes?.ok ? await trustLedgerRes.json() : null;
      const trustTxnJson = trustTxnRes?.ok ? await trustTxnRes.json() : null;

      // Guard against stale responses if matterId changed
      if (matterIdRef.current !== matterId) return;

      const expData: Expense[] = expJson.data || [];
      const teData: TimeEntry[] = teJson.data || [];
      const billData: Bill[] = billJson.data || [];
      const trustLedgerData: ClientTrustLedger[] = trustLedgerJson?.data || [];
      const trustTxnData: TrustTransaction[] = trustTxnJson?.data || [];

      setExpenses(expData);
      setTimeEntries(teData);
      setBills(billData);
      setTrustLedgers(trustLedgerData);
      setTrustTransactions(trustTxnData);

      // Compute stats
      const totalExpenses = expData.reduce((s, e) => s + Number(e.total_amount), 0);
      const billedExpenses = expData.filter(e => e.is_billed).reduce((s, e) => s + Number(e.total_amount), 0);
      const unbilledExpenses = expData.filter(e => !e.is_billed && !e.is_non_billable).reduce((s, e) => s + Number(e.total_amount), 0);

      const totalTimeAmount = teData.reduce((s, t) => s + Number(t.amount), 0);
      const totalHours = teData.reduce((s, t) => s + Number(t.duration), 0);
      const billableHours = teData.filter(t => !t.is_non_billable).reduce((s, t) => s + Number(t.duration), 0);
      const billedTimeAmount = teData.filter(t => t.is_billed).reduce((s, t) => s + Number(t.amount), 0);
      const unbilledTimeAmount = teData.filter(t => !t.is_billed && !t.is_non_billable).reduce((s, t) => s + Number(t.amount), 0);

      const billsTotal = billData.reduce((s, b) => s + Number(b.total), 0);
      const billsOutstanding = billData.filter(b => b.balance > 0).reduce((s, b) => s + Number(b.balance), 0);
      const billsPaid = billData.reduce((s, b) => s + Number(b.amount_paid), 0);

      // Trust stats
      const trustBalance = trustLedgerData.reduce((s, l) => s + Number(l.current_balance), 0);
      const trustTotalDeposits = trustLedgerData.reduce((s, l) => s + Number(l.total_deposits), 0);
      const trustTotalDisbursements = trustLedgerData.reduce((s, l) => s + Number(l.total_disbursements), 0);

      setStats({
        totalExpenses,
        unbilledExpenses,
        billedExpenses,
        expenseCount: expData.length,
        totalTimeAmount,
        totalHours,
        billableHours,
        unbilledTimeAmount,
        billedTimeAmount,
        timeEntryCount: teData.length,
        billsTotal,
        billsOutstanding,
        billsPaid,
        billCount: billData.length,
        trustBalance,
        trustTotalDeposits,
        trustTotalDisbursements,
        trustTransactionCount: trustTxnData.length,
        totalWip: unbilledTimeAmount + unbilledExpenses,
        totalBilled: billedTimeAmount + billedExpenses,
      });
    } catch (err) {
      if (matterIdRef.current !== matterId) return;
      const msg = err instanceof Error ? err.message : 'Failed to fetch billing data';
      setError(msg);
    } finally {
      if (matterIdRef.current === matterId) {
        setLoading(false);
      }
    }
  }, [matterId, keycloak?.token, getHeaders]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // ── Mutation methods (auto-inject matter_id) ──

  const createTimeEntry = useCallback(
    async (dto: Omit<CreateTimeEntryDto, 'matter_id'>) => {
      const res = await fetch(`${API_BASE}/api/v1/billing/time-entries`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ ...dto, matter_id: matterId }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) throw new Error(json?.error || json?.description || 'Failed to create time entry');
      await fetchAll();
      return json.data as TimeEntry;
    },
    [getHeaders, matterId, fetchAll]
  );

  const createExpense = useCallback(
    async (dto: Omit<CreateExpenseDto, 'matter_id'>) => {
      const res = await fetch(`${API_BASE}/api/v1/billing/expenses`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ ...dto, matter_id: matterId }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) throw new Error(json?.error || json?.description || 'Failed to create expense');
      await fetchAll();
      return json.data as Expense;
    },
    [getHeaders, matterId, fetchAll]
  );

  const createManualBill = useCallback(
    async (dto?: Omit<CreateManualBillDto, 'matter_id'>) => {
      const res = await fetch(`${API_BASE}/api/v1/billing/bills/manual`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ ...(dto || {}), matter_id: matterId }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) throw new Error(json?.error || json?.description || 'Failed to create bill');
      await fetchAll();
      return json.data as Bill;
    },
    [getHeaders, matterId, fetchAll]
  );

  // ── Timer methods ──

  /** Start the timer on an existing time entry */
  const startTimer = useCallback(
    async (timeEntryId: string) => {
      const res = await fetch(`${API_BASE}/api/v1/billing/time-entries/${timeEntryId}/start-timer`, {
        method: 'POST',
        headers: getHeaders(),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) throw new Error(json?.error || json?.description || 'Failed to start timer');
      await fetchAll();
      return json.data as TimeEntry;
    },
    [getHeaders, fetchAll]
  );

  /** Pause the timer on an existing time entry */
  const pauseTimer = useCallback(
    async (timeEntryId: string) => {
      const res = await fetch(`${API_BASE}/api/v1/billing/time-entries/${timeEntryId}/pause-timer`, {
        method: 'POST',
        headers: getHeaders(),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) throw new Error(json?.error || json?.description || 'Failed to pause timer');
      await fetchAll();
      return json.data as TimeEntry;
    },
    [getHeaders, fetchAll]
  );

  /** Stop the timer on an existing time entry (auto-calculates duration) */
  const stopTimer = useCallback(
    async (timeEntryId: string) => {
      const res = await fetch(`${API_BASE}/api/v1/billing/time-entries/${timeEntryId}/stop-timer`, {
        method: 'POST',
        headers: getHeaders(),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) throw new Error(json?.error || json?.description || 'Failed to stop timer');
      await fetchAll();
      return json.data as TimeEntry;
    },
    [getHeaders, fetchAll]
  );

  /** Create a time entry and immediately start the timer on it */
  const createAndStartTimer = useCallback(
    async (dto: Omit<CreateTimeEntryDto, 'matter_id'>) => {
      // Create with duration 0 — timer will track elapsed time
      const entry = await createTimeEntry({ ...dto, duration: 0 });
      return startTimer(entry.time_entry_id);
    },
    [createTimeEntry, startTimer]
  );

  /** Find the currently running timer (if any) from fetched time entries */
  const activeTimer: TimeEntry | null = timeEntries.find(
    (te) => te.timer_status === 'running' || te.timer_status === 'paused'
  ) || null;

  // ── Trust mutation methods ──

  const recordTrustDeposit = useCallback(
    async (dto: RecordTrustDepositDto) => {
      const res = await fetch(`${API_BASE}/api/v1/billing/trust-transactions/deposit`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ ...dto, matter_id: matterId }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) throw new Error(json?.description || json?.error || 'Failed to record trust deposit');
      await fetchAll();
      return json.data as TrustTransaction;
    },
    [getHeaders, matterId, fetchAll]
  );

  const recordTrustRefund = useCallback(
    async (dto: RecordTrustRefundDto) => {
      const res = await fetch(`${API_BASE}/api/v1/billing/trust-transactions/refund`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(dto),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) throw new Error(json?.description || json?.error || 'Failed to record trust refund');
      await fetchAll();
      return json.data as TrustTransaction;
    },
    [getHeaders, fetchAll]
  );

  const recordFeeTransfer = useCallback(
    async (dto: RecordFeeTransferDto) => {
      const res = await fetch(`${API_BASE}/api/v1/billing/trust-transactions/fee-transfer`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(dto),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) throw new Error(json?.description || json?.error || 'Failed to record fee transfer');
      await fetchAll();
      return json.data as TrustTransaction;
    },
    [getHeaders, fetchAll]
  );

  /** Primary trust ledger for this matter (first active ledger) */
  const trustLedger: ClientTrustLedger | null = trustLedgers.find(l => l.status === 'active') || null;

  return {
    expenses,
    timeEntries,
    bills,
    trustLedgers,
    trustTransactions,
    loading,
    error,
    stats,
    refresh: fetchAll,
    createTimeEntry,
    createExpense,
    createManualBill,
    // Timer
    startTimer,
    pauseTimer,
    stopTimer,
    createAndStartTimer,
    activeTimer,
    // Trust
    trustLedger,
    recordTrustDeposit,
    recordTrustRefund,
    recordFeeTransfer,
  };
}
